import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { NESTED_ROW_ID_DELIMITER } from "PFComponents/table/parts/nested_table_row.utils";
import { NotificationSetting } from "PFTypes";
import { useCallback, useMemo, useState } from "react";

import { NotificationRow } from "./notifications_settings_form.types";

type UseNotificationRowSelectionReturn = {
  rowSelection: RowSelectionState;
  handleRowSelectionChange: OnChangeFn<RowSelectionState>;
  selectedSettings: NotificationSetting[];
  clearSelection: () => void;
};

export const useNotificationRowSelection = (
  notificationsSettings: NotificationSetting[],
  allRows: NotificationRow[]
): UseNotificationRowSelectionReturn => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const groupChildKeys = useMemo<Record<string, string[]>>(
    () =>
      Object.fromEntries(
        allRows
          .filter((row) => row.group !== null)
          .map((row) => [
            row.rowId,
            row.childrenTableData.map((child) => `${row.rowId}${NESTED_ROW_ID_DELIMITER}${child.rowId}`)
          ])
      ),
    [allRows]
  );

  const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
    (updater) => {
      setRowSelection((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        const result: RowSelectionState = { ...next };

        Object.entries(groupChildKeys)
          .filter(([groupRowId]) => next[groupRowId] !== prev[groupRowId])
          .forEach(([groupRowId, childKeys]) => {
            const selected = !!next[groupRowId];
            childKeys.forEach((childKey) => {
              if (selected) {
                result[childKey] = true;
              } else {
                delete result[childKey];
              }
            });
          });

        return result;
      });
    },
    [groupChildKeys]
  );

  const selectedSettings = useMemo(() => {
    const selectedIds = Object.entries(rowSelection)
      .filter(([, selected]) => selected)
      .map(([key]) => {
        const id = key.lastIndexOf(NESTED_ROW_ID_DELIMITER);
        return id === -1 ? key : key.slice(id + 1);
      });

    return notificationsSettings.filter(({ id }) => selectedIds.includes(String(id)));
  }, [rowSelection, notificationsSettings]);

  const clearSelection = useCallback(() => setRowSelection({}), []);

  return { rowSelection, handleRowSelectionChange, selectedSettings, clearSelection };
};
